import { useAuth0 } from "@auth0/auth0-react"
import { useDispatch, useSelector } from "react-redux"
import axios from "axios"
import { 
	setFutureSchedulesData, 
	setFutureSchedulesLoading, 
	setFutureSchedulesError 
} from "../../features/futureHuddles/futureSchedulesSlice"
import { useEffect, useRef } from "react"

/**
 * Fetch future schedule data (patients and nurses) for one or more days
 * @param {Object} settingsData - Settings data containing center open information
 * @param {String|Array} dates - Date string or array of date strings to fetch data for. If null/empty, no data will be fetched
 * @returns {Object} - { data, isLoading, error }
 */
const useFetchFutureSchedules = (settingsData, dates = null) => {
	const { user, getAccessTokenSilently } = useAuth0()
	const dispatch = useDispatch()
	const data = useSelector(state => state.futureSchedules.data)
	const status = useSelector(state => state.futureSchedules.status)
	const error = useSelector(state => state.futureSchedules.error)
	const isLoading = status === 'loading'
	const centerOpen = settingsData?.centerOpen
	const previousDates = useRef(null)
	
	useEffect(() => {
		// Normalize dates to always be an array for consistent processing
		const datesArray = dates ? (Array.isArray(dates) ? dates : [dates]) : []
		const datesString = datesArray.sort().join(',')
		
		// Check which dates we actually need to fetch (avoid re-fetching existing data)
		const datesToFetch = datesArray.filter(date => !data[date])
		
		// Only fetch if we have dates to fetch and the dates list has changed
		if (datesArray.length > 0 && previousDates.current !== datesString && datesToFetch.length > 0) {
			previousDates.current = datesString
			
			const fetchData = async () => {
				dispatch(setFutureSchedulesLoading())
				
				try {
					const token = await getAccessTokenSilently({
						audience: process.env.REACT_APP_API_IDENTIFIER
					})
					
					// Fetch schedules in batches to improve performance and user experience
					const batchSize = 5 // Process 5 dates at a time
					const scheduleResults = []
					
					for (let i = 0; i < datesToFetch.length; i += batchSize) {
						const batch = datesToFetch.slice(i, i + batchSize)
						
						const batchPromises = batch.map(async (date) => {
							try {
								const response = await axios.get(
									`${process.env.REACT_APP_BACKEND_URL}/api/future-schedules`,
									{
										params: { date },
										headers: { Authorization: `Bearer ${token}` }
									}
								)
								return {
									date,
									data: response.data.payload
								}
							} catch (error) {
								console.warn(`Failed to fetch schedule for ${date}:`, error)
								return {
									date,
									data: { patients: [], nurses: [] }
								}
							}
						})

					const batchResults = await Promise.all(batchPromises)
					scheduleResults.push(...batchResults)
					
					// Update Redux store after each batch to show progressive loading
					const batchSchedulesData = {}
					batchResults.forEach((result) => {
						if (result.data) {
							batchSchedulesData[result.date] = {
								patients: result.data.patients || [],
								nurses: result.data.nurses || []
							}
						}
					})
					
					// Update store with current batch (reducer will merge with existing data)
					dispatch(setFutureSchedulesData(batchSchedulesData))
					}
					
					// Final update is already done in batches above, so we can remove this redundant code
				} catch (err) {
					dispatch(setFutureSchedulesError(err.toString()))
				}
			}
			
			fetchData()
		}
		// `data` is intentionally not in deps: the effect reads it only to skip
		// already-fetched dates, and adding it would loop (dispatch updates `data`).
		// The `previousDates` ref guards re-runs when `dates` is unchanged.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dates, user?.org_id, dispatch, getAccessTokenSilently, centerOpen])
	
	return { data, isLoading, error }
}

export default useFetchFutureSchedules
